import PropTypes from 'prop-types';
import { createContext, useContext, useMemo } from 'react';

import en from 'i18n/en.json';
import fr from 'i18n/fr.json';
// CommonJS sibling, shared with next-sitemap.config.js. Default import keeps the
// interop explicit rather than relying on webpack's named-export detection.
import routing from 'i18n/alternates';

export const DEFAULT_LOCALE = 'en';
export const LOCALES = ['en', 'fr'];

const dictionaries = { en, fr };

/** Pages that exist in both languages: EN path → FR path. */
export const TRANSLATED_ROUTES = routing.TRANSLATED_ROUTES;

/** hreflang alternates for an EN path, or `undefined` when it has no FR twin. */
export const alternatesFor = routing.alternatesFor;

/**
 * Locale of a page path (`/fr`, `/fr/pricing` → `fr`, anything else → `en`).
 * @arg {string} [path='/'] Route path.
 * @return {string}
 */
export const localeFromPath = (path = '/') =>
  /^\/fr(?=$|[/?#])/.test(path) ? 'fr' : DEFAULT_LOCALE;

/**
 * Rewrite an internal EN path to its FR twin when there is one.
 * Absolute URLs and untranslated pages are returned as-is.
 * @arg {string} path Internal path, may carry a hash or a query string.
 * @arg {string} [locale=DEFAULT_LOCALE] Target locale.
 * @return {string}
 */
export const localizePath = (path, locale = DEFAULT_LOCALE) => {
  if (locale === DEFAULT_LOCALE || typeof path !== 'string' || !path.startsWith('/')) {
    return path;
  }
  const [, pathname, suffix = ''] = path.match(/^([^?#]*)(.*)$/);
  const twin = TRANSLATED_ROUTES[pathname.replace(/(.)\/$/, '$1') || '/'];
  return twin ? `${twin}${suffix}` : path;
};

const resolve = (dict, key) =>
  key
    .split('.')
    .reduce((acc, part) => (acc && typeof acc === 'object' ? acc[part] : undefined), dict);

// Warn once per missing key. Pages are prerendered at build time, so these
// warnings surface in the `next build` output.
const warned = new Set();
const warnOnce = (message) => {
  if (warned.has(message)) {
    return;
  }
  warned.add(message);
  console.warn(`[i18n] ${message}`);
};

const interpolate = (value, vars) => {
  if (!vars) {
    return value;
  }
  if (typeof value === 'string') {
    return value.replace(/\{(\w+)\}/g, (match, name) =>
      name in vars ? String(vars[name]) : match,
    );
  }
  if (Array.isArray(value)) {
    return value.map((item) => interpolate(item, vars));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, interpolate(v, vars)]));
  }
  return value;
};

/**
 * Read a namespaced key from a dictionary, falling back to English.
 * @arg {string} locale Locale code.
 * @arg {string} key Namespaced key, e.g. `home.hero.title`.
 * @arg {object} [vars] Values for `{placeholders}`.
 * @return {string|Array|object}
 */
export const translate = (locale, key, vars) => {
  let value = resolve(dictionaries[locale] ?? dictionaries[DEFAULT_LOCALE], key);
  if (value === undefined) {
    if (locale !== DEFAULT_LOCALE) {
      warnOnce(`missing "${key}" in "${locale}", falling back to "${DEFAULT_LOCALE}"`);
    }
    value = resolve(dictionaries[DEFAULT_LOCALE], key);
  }
  if (value === undefined) {
    warnOnce(`missing "${key}" in every dictionary`);
    return key;
  }
  return interpolate(value, vars);
};

const LocaleContext = createContext(DEFAULT_LOCALE);

export const LocaleProvider = ({ locale = DEFAULT_LOCALE, children }) => (
  <LocaleContext.Provider value={LOCALES.includes(locale) ? locale : DEFAULT_LOCALE}>
    {children}
  </LocaleContext.Provider>
);

LocaleProvider.propTypes = {
  locale: PropTypes.oneOf(LOCALES),
  children: PropTypes.node,
};

/** Active locale, from the nearest `LocaleProvider`. */
export const useLocale = () => useContext(LocaleContext);

/**
 * Translation helper bound to the active locale.
 * `t(key, vars)` reads the dictionary, `t.path(path)` localizes an internal link,
 * `t.locale` is the resolved locale.
 * @arg {string} [locale] Override the locale from the context.
 * @return {function}
 */
export const useT = (locale) => {
  const contextLocale = useLocale();
  const active = LOCALES.includes(locale) ? locale : contextLocale;
  return useMemo(() => {
    const t = (key, vars) => translate(active, key, vars);
    t.locale = active;
    t.path = (path) => localizePath(path, active);
    return t;
  }, [active]);
};
